// src/components/lesson/diagrams/HighlightedPassage.tsx
//
// Renders a passage with key clue-words highlighted in colored spans.
// Used for RW lessons to teach "look for these specific words":
// transition signals, hedging words, contrast markers, etc.
//
// Params:
//   {
//     "text": "However, the productivity equivalence held only when...",
//     "highlights": [
//       { "phrase": "However", "color": "amber",  "label": "contrast signal" },
//       { "phrase": "only when", "color": "cyan",  "label": "conditional / hedge" }
//     ]
//   }
//
// Each highlight finds ALL occurrences of `phrase` (case-insensitive,
// whole-word) and wraps them in a styled <mark> with an underline-tooltip
// for the label.
//
// Colors:
//   - cyan    → conditional / hedge / qualifier
//   - amber   → contrast / turn / signal of change
//   - emerald → conclusion / inference target
//   - rose    → trap / wrong-answer hint

type Highlight = {
  phrase: string
  color?: 'cyan' | 'amber' | 'emerald' | 'rose'
  label?: string
}

type HighlightedPassageParams = {
  text?: string
  highlights?: Highlight[]
  /** Optional title shown above the passage. */
  title?: string
  label?: string
}

const COLOR_CLASSES: Record<string, string> = {
  cyan: 'bg-brand/20 text-brand-ink border-brand/40',
  amber: 'bg-amber-500/20 text-amber-900 dark:text-amber-100 border-amber-500/40',
  emerald: 'bg-emerald-500/20 text-emerald-900 dark:text-emerald-100 border-emerald-500/40',
  rose: 'bg-rose-500/20 text-rose-900 dark:text-rose-100 border-rose-500/40',
}

const COLOR_DOT: Record<string, string> = {
  cyan: 'bg-brand',
  amber: 'bg-amber-400',
  emerald: 'bg-emerald-400',
  rose: 'bg-rose-400',
}

export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'hl'; phrase: string; color: string; label?: string; matched: string }

/**
 * Build a single linear list of segments by repeatedly matching each
 * highlight phrase. Uses a greedy left-to-right pass so non-overlapping
 * highlights all show up. Earlier-listed highlights take priority on
 * ties.
 */
export function buildSegments(text: string, highlights: Highlight[]): Segment[] {
  type Match = { start: number; end: number; hl: Highlight; matched: string }

  const matches: Match[] = []
  for (const hl of highlights) {
    if (!hl.phrase) continue
    const escaped = hl.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const re = new RegExp(`\\b${escaped}\\b`, 'gi')
    let m: RegExpExecArray | null
    while ((m = re.exec(text)) !== null) {
      matches.push({
        start: m.index,
        end: m.index + m[0].length,
        hl,
        matched: m[0],
      })
    }
  }

  matches.sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start))

  const kept: Match[] = []
  let lastEnd = -1
  for (const m of matches) {
    if (m.start >= lastEnd) {
      kept.push(m)
      lastEnd = m.end
    }
  }

  const segs: Segment[] = []
  let cursor = 0
  for (const m of kept) {
    if (m.start > cursor) {
      segs.push({ kind: 'text', text: text.slice(cursor, m.start) })
    }
    segs.push({
      kind: 'hl',
      phrase: m.hl.phrase,
      color: m.hl.color ?? 'cyan',
      label: m.hl.label,
      matched: m.matched,
    })
    cursor = m.end
  }
  if (cursor < text.length) {
    segs.push({ kind: 'text', text: text.slice(cursor) })
  }
  return segs
}

export default function HighlightedPassage({ params }: { params: Record<string, unknown> }) {
  const p = params as HighlightedPassageParams
  const text = typeof p.text === 'string' ? p.text : ''
  const highlights = Array.isArray(p.highlights)
    ? (p.highlights as Highlight[]).filter((h) => h && typeof h.phrase === 'string')
    : []

  if (!text) return null

  const segs = buildSegments(text, highlights)

  // Build legend from the colors actually used (deduped by color+label)
  const legendKey = (h: Highlight) => `${h.color ?? 'cyan'}::${h.label ?? ''}`
  const legendMap = new Map<string, Highlight>()
  for (const h of highlights) {
    if (h.label) legendMap.set(legendKey(h), h)
  }
  const legend = Array.from(legendMap.values())

  return (
    <figure
      // #44: no role="img" — the passage body IS the content, so screen
      // readers must read the text (and its <mark> highlights) natively
      // rather than collapsing it into an opaque image label.
      aria-label={p.label ?? p.title ?? 'Highlighted passage'}
      className="my-3 rounded-md border border-border bg-card/60 p-4"
    >
      {p.title && (
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {p.title}
        </div>
      )}
      <div className="text-[15px] leading-7 text-foreground">
        {segs.map((s, i) => {
          if (s.kind === 'text') return <span key={i}>{s.text}</span>
          const cls = COLOR_CLASSES[s.color] ?? COLOR_CLASSES.cyan
          return (
            <mark
              key={i}
              className={`mx-0.5 rounded border px-1 py-0.5 font-medium ${cls}`}
              title={s.label ?? undefined}
            >
              {s.matched}
            </mark>
          )
        })}
      </div>
      {legend.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
          {legend.map((h, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              <span
                className={`inline-block h-2 w-2 rounded-full ${COLOR_DOT[h.color ?? 'cyan']}`}
              />
              {h.label}
            </span>
          ))}
        </div>
      )}
    </figure>
  )
}
